import type {
  AnalyticsResponse,
  Campaign,
  CampaignRequestsResponse,
  MeResponse,
  NotificationItem,
  Product,
  SampleRequest,
} from "../types/supplier";

export const demoMe: MeResponse = {
  user: {
    id: "demo-supplier",
    phoneNumber: "010-0000-0000",
    supplierMemberships: [
      {
        id: "demo-membership",
        status: "active",
        company: {
          companyName: "미들웨어랩",
          brandName: "Daily Ground",
          contactName: "김키트",
          contactEmail: "supplier@example.com",
          businessRegistrationNumber: "123-45-67890",
          representativeName: "김대표",
          openingDate: "2024-01-15",
          businessAddress: "서울특별시 강남구 테헤란로 123",
          businessType: "도소매업",
          businessItem: "생활용품",
          websiteUrl: "https://kithub24.com",
        },
      },
    ],
  },
};

export const demoNotifications: NotificationItem[] = [
  {
    id: "demo-notification-1",
    title: "새로운 키트 신청이 도착했습니다.",
    body: "데일리 밸런스 캠페인에 3명의 키트 신청 사용자가 추가되었습니다.",
    isRead: false,
    data: { type: "sample_request_created" },
    createdAt: new Date().toISOString(),
  },
  {
    id: "demo-notification-2",
    title: "설문 답변이 도착했습니다.",
    body: "사용자가 남긴 설문 답변을 확인해보세요.",
    isRead: true,
    data: { type: "analytics_updated" },
    createdAt: new Date(Date.now() - 86_400_000).toISOString(),
  },
];

export const demoProducts: Product[] = [
  {
    id: "demo-product-1",
    name: "데일리 밸런스 프로바이오틱스",
    brandName: "Daily Ground",
    status: "approved",
    categories: ["health_food"],
    mainImageUrl: "/og-image-1200x630.png",
    sampleImageUrl: "/og-image-1200x630.png",
    shortDescription: "매일 간편하게 섭취하는 장 건강 프로바이오틱스",
    purchaseMode: "external_url",
    purchaseUrl: "https://kithub24.com",
    surveyResponseCount: 67,
    survey: { title: "샘플 수령 설문" },
    createdAt: new Date(Date.now() - 30 * 86_400_000).toISOString(),
  },
  {
    id: "demo-product-2",
    name: "센서티브 카밍 세럼",
    brandName: "Mellow Lab",
    status: "pending",
    categories: ["beauty"],
    mainImageUrl: "/og-image-1200x630.png",
    sampleImageUrl: "/og-image-1200x630.png",
    shortDescription: "민감 피부를 위한 저자극 수분 진정 세럼",
    purchaseMode: "external_url",
    surveyResponseCount: 18,
    survey: { title: "제품 사용 설문" },
    createdAt: new Date(Date.now() - 12 * 86_400_000).toISOString(),
  },
  {
    id: "demo-product-3",
    name: "제로웨이스트 주방 클리너",
    brandName: "Green Table",
    status: "approved",
    categories: ["living"],
    mainImageUrl: "/og-image-1200x630.png",
    shortDescription: "식물 유래 성분으로 만든 주방 다목적 클리너",
    purchaseMode: "external_url",
    surveyResponseCount: 31,
    survey: { title: "사용성 설문" },
    createdAt: new Date(Date.now() - 21 * 86_400_000).toISOString(),
  },
];

export const demoCampaigns: Campaign[] = [
  {
    id: "demo-campaign-1",
    productId: "demo-product-1",
    status: "active",
    totalQuantity: 120,
    requestedCount: 96,
    availableQuantity: 24,
    requestedStatusCount: 11,
    preparingCount: 8,
    riderAssignedStatusCount: 4,
    shippingCount: 6,
    deliveredCount: 52,
    receivedConfirmedCount: 48,
    surveyCompletedCount: 42,
    notReceivedReportedCount: 1,
    cancelledCount: 2,
    receiptPhotoRequired: true,
    fullProductRewardEnabled: true,
    fullProductRewardCount: 10,
    sampleSizeText: "7일분",
    regionScope: { type: "nationwide", label: "전국" },
    startsAt: new Date(Date.now() - 10 * 86_400_000).toISOString(),
  },
  {
    id: "demo-campaign-2",
    productId: "demo-product-2",
    status: "pending",
    totalQuantity: 80,
    requestedCount: 32,
    availableQuantity: 48,
    requestedStatusCount: 12,
    preparingCount: 5,
    shippingCount: 3,
    deliveredCount: 12,
    receivedConfirmedCount: 10,
    surveyCompletedCount: 8,
    receiptPhotoRequired: false,
    fullProductRewardEnabled: false,
    fullProductRewardCount: 0,
    sampleSizeText: "10ml",
    regionScope: { type: "sido", label: "서울특별시 전체" },
    startsAt: new Date(Date.now() + 3 * 86_400_000).toISOString(),
  },
  {
    id: "demo-campaign-3",
    productId: "demo-product-3",
    status: "sold_out",
    totalQuantity: 50,
    requestedCount: 50,
    availableQuantity: 0,
    preparingCount: 2,
    shippingCount: 4,
    deliveredCount: 38,
    receivedConfirmedCount: 36,
    surveyCompletedCount: 31,
    receiptPhotoRequired: true,
    fullProductRewardEnabled: true,
    fullProductRewardCount: 5,
    regionScope: { type: "nationwide", label: "전국" },
    startsAt: new Date(Date.now() - 22 * 86_400_000).toISOString(),
  },
];

const statuses = [
  "requested",
  "preparing",
  "rider_assigned",
  "shipping",
  "delivered",
  "received_confirmed",
  "survey_completed",
];
const regions = [
  ["서울특별시", "강남구", "역삼동"],
  ["서울특별시", "마포구", "서교동"],
  ["경기도", "성남시", "정자동"],
  ["부산광역시", "해운대구", "우동"],
  ["대전광역시", "유성구", "봉명동"],
];

function requests(count: number, prefix: string): SampleRequest[] {
  return Array.from({ length: count }, (_, index) => {
    const status = statuses[index % statuses.length];
    const region = regions[index % regions.length];
    const surveyDone = status === "survey_completed";
    return {
      id: `${prefix}-request-${index + 1}`,
      status,
      applicantLabel: `사용자 ${String(index + 1).padStart(3, "0")}`,
      requestedAt: new Date(
        Date.now() - (count - index) * 3_600_000,
      ).toISOString(),
      sidoNameSnapshot: region[0],
      sigunguNameSnapshot: region[1],
      bnameSnapshot: region[2],
      riderAssignedAt:
        index % 3 === 0
          ? new Date(Date.now() - 4 * 86_400_000).toISOString()
          : undefined,
      deliveredAt:
        ["delivered", "received_confirmed", "survey_completed"].includes(status)
          ? new Date(Date.now() - 2 * 86_400_000).toISOString()
          : undefined,
      receivedConfirmedAt:
        ["received_confirmed", "survey_completed"].includes(status)
          ? new Date(Date.now() - 36 * 3_600_000).toISOString()
          : undefined,
      surveyCompletedAt: surveyDone
        ? new Date(Date.now() - 18 * 3_600_000).toISOString()
        : undefined,
      surveySubmittedAt: surveyDone
        ? new Date(Date.now() - 18 * 3_600_000).toISOString()
        : undefined,
      surveyDurationMs: surveyDone ? 128_000 + index * 1_500 : undefined,
      avgViewDurationMs: 45_000 + index * 1_200,
      beforeRequestViewCount: 2 + (index % 5),
      afterRequestViewCount: 1 + (index % 4),
      afterDeliveryViewCount: surveyDone ? 2 + (index % 3) : 0,
      experienceScore: 68 + (index % 30),
      experienceGrade: index % 3 === 0 ? "A" : index % 3 === 1 ? "B" : "C",
      applicantFavorite: index % 4 === 0,
      panelProfile: {
        ageRange: index % 2 ? "twenties_late" : "thirties",
        gender: index % 3 ? "female" : "male",
      },
      surveyAnswerItems: surveyDone
        ? [
            {
              key: "satisfaction",
              title: "제품 만족도",
              value: `${4 + (index % 2)}점`,
            },
            {
              key: "repurchase_intent",
              title: "구매 의향",
              value: index % 3 ? "구매 의향 있음" : "고민 중",
            },
            {
              key: "feedback",
              title: "사용 의견",
              value: "사용감이 편하고 패키지가 깔끔했습니다.",
            },
          ]
        : [],
      receiptPhotoUrl:
        index % 3 === 0 && status !== "requested"
          ? "/og-image-1200x630.png"
          : undefined,
      fullProductRewardSelected: index < 5,
    };
  });
}

export const demoRequestsByCampaign: Record<string, SampleRequest[]> = {
  "demo-campaign-1": requests(24, "daily"),
  "demo-campaign-2": requests(13, "serum"),
  "demo-campaign-3": requests(18, "cleaner"),
};

export function demoCampaignRequests(
  campaignId: string,
): CampaignRequestsResponse {
  const campaign =
    demoCampaigns.find((item) => item.id === campaignId) ?? demoCampaigns[0];
  return {
    campaign,
    fullProductRewardSelectionReady: true,
    panelStats: {
      totalUserCount: 143,
      fieldRows: [
        { fieldKey: "purchaseInfoSources", optionCode: "user_reviews", userCount: 92 },
        { fieldKey: "purchaseInfoSources", optionCode: "detail_page", userCount: 67 },
        { fieldKey: "purchaseTendency", optionCode: "wait_for_reviews", userCount: 58 },
        { fieldKey: "purchaseTendency", optionCode: "need_based_search", userCount: 44 },
        { fieldKey: "repurchaseReason", optionCode: "convenience", userCount: 61 },
        { fieldKey: "selectionPriority", optionCode: "reviews", userCount: 76 },
        { fieldKey: "dissatisfactionReason", optionCode: "poor_value", userCount: 39 },
        { fieldKey: "purchaseChannel", optionCode: "online_mall", userCount: 81 },
        { fieldKey: "productCategories", optionCode: "beauty", userCount: 49 },
        { fieldKey: "householdType", optionCode: "single", userCount: 52 },
        { fieldKey: "petStatus", optionCode: "none", userCount: 88 },
        { fieldKey: "gender", optionCode: "female", userCount: 95 },
        { fieldKey: "ageRange", optionCode: "thirties", userCount: 54 },
      ],
    },
    items: demoRequestsByCampaign[campaign.id] ?? [],
  };
}

export const demoAnalytics: AnalyticsResponse = {
  items: demoProducts.map((product, index) => ({
    productId: product.id,
    productName: product.name,
    favoriteCount: 74 - index * 13,
    productViewCount: 1820 - index * 340,
    purchaseClickCount: 286 - index * 51,
    purchaseClickUserCount: 213 - index * 37,
    shareClickCount: 94 - index * 14,
    sampleApplyClickCount: 216 - index * 32,
    sampleRequestCount: demoCampaigns[index].requestedCount,
    sampleRequestUserCount: demoCampaigns[index].requestedCount,
    sampleTotalQuantity: demoCampaigns[index].totalQuantity,
    sampleRequestedCount: demoCampaigns[index].requestedCount,
    sampleAvailableQuantity: demoCampaigns[index].availableQuantity,
    sampleShippingCount: demoCampaigns[index].shippingCount,
    sampleDeliveredCount: demoCampaigns[index].deliveredCount,
    surveyResponseCount: demoCampaigns[index].surveyCompletedCount,
    surveyDurationAvgMs: 142_000 + index * 13_000,
  })),
  users: [],
  panelStats: { totalUserCount: 143 },
};

export function demoDelay<T>(value: T, milliseconds = 120): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), milliseconds);
  });
}
